import { Form, Formik, FormikProps } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { AppContext } from '../../../appContext/AppContext';
import { APP_TIMESTAMP_FORMAT, CODE, ORDER_TYPE_ID, REQUEST_FROM, REQUEST_TYPE, RESPONSE_MESSAGE } from '../../../utils/Constant';
import { localStorageItem } from '../../../utils/LocalStorage';
import { IKeVatTuRequest, IPhieuChiDinhVatTuItem } from '../../models/VatTuModels';
import { getDetailDonThuoc, saveDonThuoc, saveDonThuocDVKT } from '../../services/ThuocService';
import VatTuHeader from './components/header-vat-tu';
import ThongTinKeVatTu from './components/thong-tin-ke-vat-tu';
import TreeVatTu from './components/tree-vat-tu';
import { useKeVatTuContext } from './context';
import './style.scss';
import { getThongTinChanDoan } from '../../services/KhamBenhService';
import { formatDateTime } from '../../../utils/FormatUtils';
import { WAREHOUSE_TYPE } from '../../constants/Thuoc';
import { WAREHOUSE_SLIP_TYPE } from '../../constants/VatTu';
import { removeEventEnter } from '../../../utils/AppFunction';
import { KEY_LOCALSTORAGE } from 'src/app/modules/auth/core/_consts';

type Props = {
	onClose: () => void;
	itemEdit?: any;
	onSave?: () => void;
	generalInfo?: any;
};

const FormKeVatTu = ({ onClose, itemEdit, onSave, generalInfo }: Props) => {
	const { vatTuChecked, treeValue, setVatTuChecked, setTreeValue, deletedList } = useKeVatTuContext();
	const { setIsLoading } = useContext(AppContext);

	const formikRef = useRef<FormikProps<any>>(null);

	const [donVatTuDetail, setDonVatTuDetail] = useState<any>();

	const { encounterDiagnosis, visit, encounter, patient, orderId, isConclusion, fulfillDeptName, forDVKT, isReturn } = generalInfo ?? {};
	const userInfo = localStorageItem.get('current-user');
	const userMetaData = localStorageItem.get(KEY_LOCALSTORAGE.USER_METADATA);
	const validationSchema = Yup.object().shape({});
	const initialValues = {
		ngayYLenh: formatDateTime(new Date(), APP_TIMESTAMP_FORMAT),
		thuTien: true,
		provisionalDiagnosis: encounterDiagnosis?.provisionalDiagnosis,
		provisionalDiagnosisName: encounterDiagnosis?.provisionalDiagnosisName,
		comorbidity: encounterDiagnosis?.comorbidity,
	};

	const handleGetThongTinChanDoan = async () => {
		try {
			const { data } = await getThongTinChanDoan(visit?.id);
			if (data?.code === CODE.SUCCESS) {
				formikRef.current &&
					formikRef.current.setValues({
						...initialValues,
						provisionalDiagnosis: data?.data?.[isConclusion ? 'icdXPrimary' : 'provisionalDiagnosis'] || '',
						provisionalDiagnosisName: data?.data?.[isConclusion ? 'icdXPrimaryName' : 'provisionalDiagnosisName'] || '',
						comorbidity: data?.data?.comorbidity || '',
						diagnosisNonCoded: data?.data?.diagnosisNonCoded || '',
					});
			}
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		if (itemEdit?.id) {
			getDetail(itemEdit.id);
		} else {
			handleGetThongTinChanDoan();
		}
	}, [itemEdit?.id]);

	const handleSubmit = async (values: any) => {
		try {
			if (!vatTuChecked.filter(row => !row.deleted).length) {
				toast.warn('Chưa có vật tư được kê cho đơn này');
				return;
			}

			if (!values?.provisionalDiagnosisName && !values?.diagnosisNonCoded) {
				toast.warn('Chưa nhập thông tin chẩn đoán!');
				return;
			}

			const emptyQuantityRow = vatTuChecked.find(row => !row.soLuong);
			if (emptyQuantityRow) {
				toast.warn(
					`Vật tư với mã vật tư ${emptyQuantityRow.maVatTu} chưa nhập số lượng kê`
				);
				return;
			}

			setIsLoading(true);

			const phieuChiDinhList: IPhieuChiDinhVatTuItem[] = vatTuChecked.map(row => {
				return {
					...row,
					id: row.idBackend ?? null,
					termId: row.termId,
					visitId: visit.id,
					patientId: patient.id,
					encounterId: encounter?.id,
					orderId: row.orderId,
					orderTypeId: parseInt(ORDER_TYPE_ID.VAT_TU),
					medicalItemId: row.idVatTu,
					medicalItemCode: row.maVatTu,
					medicalItemName: row.tenVatTu,
					name: row.tenVatTu,
					unit: row.donVi,
					totalQuantity: row.soLuong,
					deleted: row.deleted,
					visitObjectTypeId: visit.visitObjectTypeId,
					visitObjectTypeName: visit.visitObjectTypeName,
					visitObjectTypeCode: visit.visitObjectTypeCode,
					unitPrice: row.donGia,
					intoMoney: row.donGia * row.soLuong,
					bidId: row.bidId,
					serialNumber: row.serialNumber,
					consignment: row.consignment,
					percentIns: row.rateHealthInsurance || row.percentIns
				};
			});
			const warehouseSlipType =
				WAREHOUSE_TYPE.NORMAL === treeValue?.warehouseType
					? WAREHOUSE_SLIP_TYPE.SUPPLY
					: WAREHOUSE_SLIP_TYPE.INS_AND_INPATIENT;
			
			const requestData: IKeVatTuRequest & { type?: number | string, pid?: string } = {
				id: donVatTuDetail?.id ?? null,
				visitId: visit.id,
				patientId: patient.id,
				encounterId: encounter?.id,
				orderId: orderId,
				orderTypeId: parseInt(ORDER_TYPE_ID.VAT_TU),
				doctorOrderDay: values.ngayYLenh,
				treatmentDoctorUser: userInfo.sub,
				treatmentDoctorName: userInfo.name,
				designatedPerson: userInfo.name,
				designatedPlace: fulfillDeptName,
				designatedDate: formatDateTime(new Date(), APP_TIMESTAMP_FORMAT),
				lstMedicalItemOrder: phieuChiDinhList.concat(Array.from(deletedList.values())),
				comorbidity: values.comorbidity || encounterDiagnosis?.comorbidity,
				provisionalDiagnosis:
					values.provisionalDiagnosis || encounterDiagnosis?.provisionalDiagnosis,
				provisionalDiagnosisName:
					values.provisionalDiagnosisName || encounterDiagnosis?.provisionalDiagnosisName,
				diagnosisNonCoded:
					values.diagnosisNonCoded || encounterDiagnosis?.diagnosisNonCoded,
				warehouseId: treeValue.id,
				warehouseName: treeValue.name,
				warehouseType: treeValue.warehouseType,
				warehouseSlipType,
				...(generalInfo?.forDVKT && {
					type: parseInt(ORDER_TYPE_ID.VAT_TU)
				}),
				formCode: !forDVKT ? REQUEST_FROM.KHAM_BENH : isReturn ? REQUEST_FROM.HOAN_TRA : REQUEST_FROM.HAO_PHI,
				medicalItemTypeCode: REQUEST_TYPE.VAT_TU,
				visitObjectTypeCode: visit?.visitObjectTypeCode,
				pid: userMetaData?.personId,
			};

			const response = generalInfo?.forDVKT ? await saveDonThuocDVKT(requestData, null, orderId, generalInfo?.isReturn) : await saveDonThuoc(requestData);
			if (response.code === CODE.SUCCESS) {
				toast.success(
					(donVatTuDetail?.id || generalInfo?.forDVKT)
						? RESPONSE_MESSAGE.UPDATE.SUCCESS
						: RESPONSE_MESSAGE.ADD.SUCCESS
				);
				onSave && onSave();
				onClose();
			} else {
				let errorMessage = response?.data?.[0]?.errorMessage || RESPONSE_MESSAGE.ERROR;
				toast.error(errorMessage);
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
			setIsLoading(false);
		}
	};

	const getDetail = async (donVatTuId: string) => {
		try {
			setIsLoading(true);
			const { code, data } = await getDetailDonThuoc(donVatTuId, !generalInfo?.forDVKT ? orderId : null);

			if (CODE.SUCCESS === code) {
				setDonVatTuDetail(data);
				setTreeValue({
					id: data.warehouseId,
					name: data.warehouseName,
				});
				formikRef.current?.setValues({
					...formikRef.current.initialValues,
					ngayYLenh: data.doctorOrderDay,
					provisionalDiagnosis: data.provisionalDiagnosis,
					provisionalDiagnosisName: data.provisionalDiagnosisName,
					comorbidity: data.comorbidity,
				});

				if (data.lstMedicalItemOrderRes?.length) {
					const listVatTuSelected = data.lstMedicalItemOrderRes.map(
						(row: any, index: number) => ({
							...row,
							idBackend: row.id,
							maVatTu: row.medicalItemCode,
							tenVatTu: row.name,
							idVatTu: row.medicalItemId,
							donVi: row.unit,
							soLuong: row.totalQuantity,
							donGia: row.unitPrice,
							thanhTien: row.intoMoney,
							doiTuong: row.visitObjectTypeName || visit.visitObjectTypeName,
							index,
							servicePrice: row.svcFee,
							hospitalPrice: row.svcFeeHospital,
							bhytPrice: row.svcFeeIns,
						})
					);
					setVatTuChecked(listVatTuSelected);
				}
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Formik<any>
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={handleSubmit}
			innerRef={formikRef}
		>
			<Form className="spaces h-calc-vh-50" onKeyDown={removeEventEnter}>
				<div className="h-100">
					<Modal.Header className="py-3 header-modal">
						<VatTuHeader onClose={onClose} benhNhanInfo={{
							patient, visit
						} as any} />
					</Modal.Header>

					<Modal.Body className="px-0 py-6 spaces overflow-hidden h-calc-vh-90">
						<div className="d-flex h-100">
							<div className="spaces width-20 bg-white radius-2 d-flex flex-column min-w-200">
								<TreeVatTu donVatTuDetail={donVatTuDetail} 
									disableSelectKhoVatTu={donVatTuDetail?.id || vatTuChecked.length}
									generalInfo={generalInfo}
								/>
							</div>
							<div className="d-flex flex-column spaces width-80 bg-light radius-2">
								<ThongTinKeVatTu generalInfo={generalInfo} />
							</div>
						</div>
					</Modal.Body>
				</div>
			</Form>
		</Formik>
	);
};

export default FormKeVatTu;
